/**
 * The **provider report** contract: the shape Core answers in when it fetches a
 * financial statement live from the connected accounting system.
 *
 * A **provider report** is fetched live from the connected accounting system and
 * is that system's own figures. An OCTA **financial statement** is computed from
 * OCTA journal entries by `server/domains/gl/application/generate-statements.ts`
 * and is a different number set under a similar name. Nothing on this contract
 * may ever be filled from OCTA journals: OCTA is not the source-of-truth general
 * ledger, and a silent fallback would put two different answers under one label.
 *
 * Nothing here is provider-shaped. The raw QuickBooks `Header`/`Columns`/`Rows`
 * payload (`shared/qbo-report.ts`) never crosses this boundary - a client reads
 * the statement tree below or it reads a typed error, never provider JSON.
 *
 * @spec PROVIDER_REPORTS - `spec/integration/PROVIDER_REPORTS.md`
 * @spec MONEY M1 - every monetary value is a decimal string paired with its
 *   currency code.
 * @spec MONEY M16 - `Money` is the representation at every boundary; a report
 *   with no currency fails rather than carrying the `XXX` sentinel.
 */

import { z } from "zod";

import type { ErrorCode } from "@shared/errors";
import type { MoneyJSON } from "@shared/money";

// ─── report identity ─────────────────────────────────────────────────────────

/**
 * The three statements this surface serves. A closed union, deliberately
 * excluding `general_ledger`, `transaction_list` and `profit_and_loss_detail`:
 * a trial balance is an account listing with debit and credit totals, NOT a
 * transaction listing, and reaching for Intuit's `GeneralLedger` report to fill
 * the Trial Balance tile would answer a different question.
 */
export const PROVIDER_REPORT_TYPES = ["trial_balance", "profit_and_loss", "balance_sheet"] as const;

export type ProviderReportType = (typeof PROVIDER_REPORT_TYPES)[number];

/** Parses an incoming report type. An unknown value is rejected, never defaulted. */
export const providerReportTypeSchema = z.enum(PROVIDER_REPORT_TYPES);

/**
 * The provider a report was fetched from, as the Apideck Vault service id.
 *
 * QuickBooks **Online** only. `quickbooks-desktop` is a different product with an
 * unverified report surface and must never inherit this one by prefix match.
 */
export const PROVIDER_REPORT_PROVIDERS = ["quickbooks"] as const;

export type ProviderReportProviderId = (typeof PROVIDER_REPORT_PROVIDERS)[number];

// ─── filters ─────────────────────────────────────────────────────────────────

/**
 * Every query parameter the QuickBooks Reports GET accepts on at least one of
 * the three statements. Which keys are legal on WHICH report is decided by the
 * per-reportType schema in the Integrations domain, not here - `item`, for
 * example, is accepted on Profit & Loss only.
 *
 * A key absent from this list is rejected with `REPORT_FILTER_UNSUPPORTED`. It
 * is never silently dropped: a control that appears to work while being ignored
 * is worse than one that refuses.
 */
export const PROVIDER_REPORT_FILTER_KEYS = [
  "start_date",
  "end_date",
  "date_macro",
  "accounting_method",
  "summarize_column_by",
  "customer",
  "vendor",
  // `class` is QuickBooks' class-tracking dimension. The wire parameter name is
  // exactly `class` however it is spelled internally.
  "class",
  "department",
  "item",
  "adjusted_gain_loss",
  // Real Intuit query parameters, kept in this union so a request carrying one
  // is refused as "not accepted on this report" rather than as an unknown key.
  // Measured unusable on all three summary reports (OCT-13863) - `sort_by` and
  // `sort_order` are ignored outright, `columns` faults the call - so no
  // per-report accepted set includes them. See `provider-report-filters.ts`.
  "sort_by",
  "sort_order",
  "columns",
] as const;

export type ProviderReportFilterKey = (typeof PROVIDER_REPORT_FILTER_KEYS)[number];

/** A filter set. Values stay verbatim strings - they are the provider's vocabulary, not OCTA's. */
export type ProviderReportFilters = Readonly<Partial<Record<ProviderReportFilterKey, string>>>;

/**
 * Intuit's full `summarize_column_by` set, confirmed against the
 * ProfitAndLoss report-entity docs. Applies to `profit_and_loss` and
 * `balance_sheet` - both can be grouped by time period or by an accounting
 * dimension (customer, vendor, class, department, employee, product/service).
 *
 * Lives here, not in the Integrations filter table, so the MCP
 * `get_accounting_report` tool refuses a grouping from the same list the
 * provider-report validator trusts (OCT-22292).
 */
export const PROVIDER_SUMMARIZE_COLUMN_BY_VALUES = [
  "Total",
  "Month",
  "Week",
  "Days",
  "Quarter",
  "Year",
  "Customers",
  "Vendors",
  "Classes",
  "Departments",
  "Employees",
  "ProductsAndServices",
] as const;

export type ProviderSummarizeColumnBy = (typeof PROVIDER_SUMMARIZE_COLUMN_BY_VALUES)[number];

/**
 * `trial_balance`'s `summarize_column_by` set, restricted to time-based
 * grouping. A trial balance is a list of general-ledger accounts with
 * debit/credit balances - it has no customer, vendor, class, department,
 * employee or product dimension to group by, so those values are rejected
 * even though they are legal on `profit_and_loss` (OCT-13762).
 */
const PROVIDER_SUMMARIZE_COLUMN_BY_TIME_ONLY = [
  "Total",
  "Month",
  "Week",
  "Days",
  "Quarter",
  "Year",
] as const satisfies readonly ProviderSummarizeColumnBy[];

/**
 * The row filters that limit a report to one QuickBooks Location
 * (`department`) or one Class (`class`), OCT-22460.
 */
export const PROVIDER_REPORT_DIMENSION_FILTER_KEYS = ["department", "class"] as const;

/**
 * Whether a report type takes the {@link PROVIDER_REPORT_DIMENSION_FILTER_KEYS}.
 * A trial balance row is a general-ledger account, not a transaction, so it
 * has no Location or Class to filter by. Lives here, like the
 * `summarize_column_by` sets, so the provider-report filter table and the MCP
 * `get_accounting_report` check read one rule.
 */
export function reportTakesDimensionFilters(reportType: ProviderReportType): boolean {
  return reportType !== "trial_balance";
}

/** The `summarize_column_by` values a report type accepts. */
export function summarizeColumnByValuesFor(reportType: ProviderReportType): readonly ProviderSummarizeColumnBy[] {
  return reportType === "trial_balance" ? PROVIDER_SUMMARIZE_COLUMN_BY_TIME_ONLY : PROVIDER_SUMMARIZE_COLUMN_BY_VALUES;
}

/** Whether `value` is a `summarize_column_by` value this report type accepts. */
export function isSummarizeColumnByFor(
  reportType: ProviderReportType,
  value: string,
): value is ProviderSummarizeColumnBy {
  return summarizeColumnByValuesFor(reportType).some((accepted) => accepted === value);
}

// ─── the statement tree ──────────────────────────────────────────────────────

/**
 * One column of the rendered statement, taken from the response's own `Columns`
 * array. QuickBooks silently returns FEWER columns than asked for, so this list
 * is what came back, never what was requested.
 */
export interface StatementColumn {
  /** Stable identity for the column: the provider's `ColType` where it has one, else its position. */
  readonly id: string;
  /** The provider's own heading, verbatim. Not translated - it is the customer's own vocabulary. */
  readonly label: string;
  /** `label` columns carry account names; `money` columns carry amounts. */
  readonly kind: "label" | "money";
}

/**
 * One cell. A money cell is always a `MoneyJSON` - a decimal string plus its ISO
 * currency - so no monetary value on this contract is ever a JS `number`.
 * `empty` is a real answer and is distinct from a zero amount.
 */
export type StatementCell =
  | { readonly kind: "money"; readonly value: MoneyJSON; }
  | { readonly kind: "text"; readonly value: string; }
  | { readonly kind: "empty"; };

/**
 * A leaf line of the statement.
 *
 * `rowType: "summary"` is a section total, which QuickBooks emits as a separate
 * `Summary` block rather than a normal row. It is carried in a section's own
 * `summary` field, never among its children, so that anything adding up a
 * section's rows does not also add its total.
 */
export interface StatementRow {
  readonly kind: "row";
  /** The provider's own label, verbatim. */
  readonly label: string;
  readonly rowType: "data" | "summary";
  /** One cell per column in `StatementBody.columns`, in that order. */
  readonly cells: readonly StatementCell[];
}

/**
 * A group of the statement. Nests to arbitrary depth - QuickBooks declares no
 * ceiling, and flattening a level would silently lose structure.
 *
 * `children` may mix rows and sub-sections, which is what the provider actually
 * emits. What is NOT representable is a single node that is both a leaf and a
 * group: `kind` discriminates, so a row can never grow children.
 */
export interface StatementSection {
  readonly kind: "section";
  /** The provider's own heading, verbatim. */
  readonly label: string;
  /** QuickBooks' machine-readable section identity (`"Income"`, `"TotalAssets"`), when it gives one. */
  readonly group: string | null;
  readonly children: readonly StatementNode[];
  /** The section's total, or `null` when the provider emitted none. Never invented. */
  readonly summary: StatementRow | null;
}

export type StatementNode = StatementRow | StatementSection;

/** The rendered statement: its columns, its tree, and its report-level total. */
export interface StatementBody {
  readonly columns: readonly StatementColumn[];
  readonly nodes: readonly StatementNode[];
  /** The report's own grand total, when the provider emitted one. */
  readonly summary: StatementRow | null;
}

// ─── the envelope ────────────────────────────────────────────────────────────

/**
 * The response shape every provider report answers in.
 *
 * A reader never has to guess which period, which basis or which vintage
 * produced the figures it is quoting: `filtersApplied` states the effective
 * filter set including defaults the caller did not send, and `fetchedAt` states
 * when QuickBooks was actually called.
 */
export interface AccountingReportEnvelope {
  readonly reportType: ProviderReportType;
  readonly provider: ProviderReportProviderId;
  /** The connection the figures came through, so two entities are never confused. */
  readonly connectionId: string;
  /**
   * The report's own ISO 4217 currency, read from the provider's header and
   * applied to every money cell. Never the `XXX` sentinel: a report whose header
   * carries no currency fails with `REPORT_SHAPE_UNSUPPORTED` (M16).
   */
  readonly currency: string;
  /**
   * When the provider was actually called, ISO 8601.
   *
   * This is the report's VINTAGE. It is **not** the connection's last sync time
   * and has nothing to do with it - these figures never came through a sync. On
   * a cache hit this stays the moment of the original call, not the moment of
   * reuse, so a reader is never told a reused answer is newer than it is.
   */
  readonly fetchedAt: string;
  /** `true` when a stored successful response was reused. Failures are never stored. */
  readonly cacheHit: boolean;
  /**
   * The filter set that actually produced these figures, including defaults the
   * use case supplied. A filter the provider did not apply does not appear here.
   */
  readonly filtersApplied: ProviderReportFilters;
  /**
   * The basis the provider says it COMPUTED on, verbatim from its own response
   * header (QuickBooks: `Header.ReportBasis`, e.g. `Accrual` / `Cash`).
   *
   * Deliberately separate from `filtersApplied.accounting_method`, which is
   * only what OCTA ASKED for. OCTA sends no basis by default, so the request
   * says nothing while the provider still states one - reading the basis off
   * the request would report "unspecified" while holding the answer.
   *
   * Absent when the provider's header omits it. Never defaulted or inferred:
   * an unstated basis is reported as unstated (the same discipline M16 applies
   * to currency).
   */
  readonly reportBasis?: string;

  /**
   * Names the shape of `body`. This epic emits exactly one kind; the
   * discriminator exists so a renderer switches rather than assuming, and so a
   * row-list report can be added later without changing this type's meaning.
   */
  readonly bodyKind: "statement";
  readonly body: StatementBody;
}

// ─── slugs ───────────────────────────────────────────────────────────────────

/**
 * The URL slug each report type is reached by. Distinct from the OCTA-journal
 * statement slugs (`trial-balance`, `profit-loss`, `balance-sheet`), which keep
 * serving OCTA journals under their existing routes.
 */
export const PROVIDER_REPORT_SLUGS: Readonly<Record<ProviderReportType, string>> = {
  trial_balance: "provider-trial-balance",
  profit_and_loss: "provider-profit-loss",
  balance_sheet: "provider-balance-sheet",
} as const;

/** The report type a slug names, or `null` when the slug is not a provider report. */
export function providerReportTypeForSlug(slug: string): ProviderReportType | null {
  for (const reportType of PROVIDER_REPORT_TYPES) {
    if (PROVIDER_REPORT_SLUGS[reportType] === slug) return reportType;
  }
  return null;
}

/**
 * Every error code this surface can return.
 *
 * The client renders one state per code and must handle all of them. That was
 * previously a hand-copied list in the detail page, and it had drifted:
 * `REPORT_PROVIDER_UNSUPPORTED` and `REPORT_TYPE_UNSUPPORTED` were absent, so
 * the two cases a reader is most likely to hit by accident - switching to an
 * entity connected to Xero, or asking for a report the connection cannot run -
 * fell through to the generic card and showed a raw server message.
 *
 * Exported so the page derives its switch from this rather than restating it.
 * `satisfies readonly ErrorCode[]` ties each entry to the platform union, so a
 * typo or a renamed code fails to compile instead of silently never matching.
 *
 * `CROSS_ENTITY_CONTEXT` is deliberately NOT here. It is the platform-wide E89
 * guard rather than something this surface defines, and the page handles it
 * alongside these.
 */
export const PROVIDER_REPORT_ERROR_CODES = [
  "ACCOUNTING_CONNECTION_REQUIRED",
  "REPORT_PROVIDER_UNSUPPORTED",
  "REPORT_TYPE_UNSUPPORTED",
  "REPORT_FILTER_UNSUPPORTED",
  "REPORT_SCOPE_MISSING",
  "REPORT_RATE_LIMITED",
  "REPORT_PROVIDER_TIMEOUT",
  "REPORT_SHAPE_UNSUPPORTED",
] as const satisfies readonly ErrorCode[];

export type ProviderReportErrorCode = (typeof PROVIDER_REPORT_ERROR_CODES)[number];
